import { Organization } from "@/model/Organization"
import {
  caregiverId,
  clinicalProtocolIndex,
  cuffSize,
  departmentId,
  deviceDistributionMethod,
  diagnosis,
  localDate,
} from "@/schemas/zod-schemas"
import { LocalDate } from "@js-joda/core"
import { z } from "zod"

const pregnancySelectionSchema = ({
  newPregnancyEddRequired,
}: {
  newPregnancyEddRequired: boolean
}) =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("existing"),
      pregnancyId: z.string().nonempty("Pregnancy is a required field"),
    }),
    z.object({
      kind: z.literal("new"),
      estimatedDateOfDelivery: localDate
        .or(z.literal(""))
        .optional()
        .refine((date) => !newPregnancyEddRequired || !!date, {
          error: "Estimated delivery date is a required field",
        }),
      deliveryDate: localDate.or(z.literal("")).optional(),
      patientOptedInClinically: z.boolean(),
    }),
  ])

const commonUpdatePrescriptionFormSchema = ({
  clinicalProtocolRequired,
  endDateRequired,
  newPregnancyEddRequired,
}: {
  clinicalProtocolRequired: boolean
  endDateRequired: boolean
  newPregnancyEddRequired: boolean
}) =>
  z.object({
    pregnancy: pregnancySelectionSchema({ newPregnancyEddRequired }),
    clinicalProtocolIndex: clinicalProtocolIndex
      .optional()
      .refine(
        (clinicalProtocolIndex) =>
          !clinicalProtocolRequired ||
          (clinicalProtocolIndex !== undefined && clinicalProtocolIndex >= 0),
        { error: "Clinical protocol is a required field" }
      ),
    diagnosis: diagnosis.or(z.literal("")).optional(),
    startDate: z
      .string()
      .nonempty("Start date is a required field")
      .pipe(localDate),
    endDate: localDate
      .or(z.literal(""))
      .optional()
      .refine((date) => !endDateRequired || date, {
        error: "End date is a required field",
      }),
    caregiverId: caregiverId.nonempty("Provider is a required field"),
    departmentId: departmentId.or(z.literal("")).optional(),
  })

export const updateDiabetesPrescriptionFormSchema = ({
  organization,
  clinicalProtocolRequired,
  endDateRequired,
  newPregnancyEddRequired,
}: {
  organization: Organization | undefined
  clinicalProtocolRequired: boolean
  endDateRequired: boolean
  newPregnancyEddRequired: boolean
}) =>
  commonUpdatePrescriptionFormSchema({
    clinicalProtocolRequired,
    endDateRequired,
    newPregnancyEddRequired,
  })
    .extend({
      deviceDistributionMethod: deviceDistributionMethod,
    })
    .refine(
      ({ departmentId }) =>
        !organization || organization.emrType !== "athena" || departmentId,
      {
        message: "Department is a required field",
        path: ["departmentId"],
      }
    )
    .refine(
      ({ startDate, endDate }) => {
        if (startDate && endDate) {
          return !LocalDate.parse(endDate).isBefore(LocalDate.parse(startDate))
        }
        return true
      },
      {
        message: "End date must be after the start date",
        path: ["endDate"],
      }
    )

export const updateHypertensionPrescriptionFormSchema = ({
  organization,
  clinicalProtocolRequired,
  endDateRequired,
  newPregnancyEddRequired,
}: {
  organization: Organization | undefined
  clinicalProtocolRequired: boolean
  endDateRequired: boolean
  newPregnancyEddRequired: boolean
}) =>
  commonUpdatePrescriptionFormSchema({
    clinicalProtocolRequired,
    endDateRequired,
    newPregnancyEddRequired,
  })
    .extend({
      cuffSize: cuffSize,
    })
    .refine(
      ({ departmentId }) =>
        !organization || organization.emrType !== "athena" || departmentId,
      {
        message: "Department is a required field",
        path: ["departmentId"],
      }
    )
    .refine(
      ({ startDate, endDate }) => {
        if (startDate && endDate) {
          return !LocalDate.parse(endDate).isBefore(LocalDate.parse(startDate))
        }
        return true
      },
      {
        message: "End date must be after the start date",
        path: ["endDate"],
      }
    )
